export const FILTERS_STORAGE_KEY = 'downtimesFilters'
export const TABLE_STORAGE_KEY = 'downtimesTableState'
export const STATE_VERSION = 7
export const PAGE_SIZE = 50
import { isKnownPeriod, resolvePeriodRange } from './downtimePeriods.mjs'
export const VALID_CLASSIFICATIONS = ['confirmed', 'incomplete', 'late', 'none', 'potential', 'upcoming']
export const SORTABLE_COLUMN_IDS = [
  'trip_id',
  'planned_date',
  'arrival_time',
  'departure_time',
  'delay_minutes',
  'overdue_hours',
  'downtime_cost',
  'manager_name',
]

export const COLUMN_IDS = [
  'downtime_classification',
  'trip_id',
  'warehouse_name',
  'manager_name',
  'operation_type',
  'planned_date',
  'downtime_start',
  'arrival_time',
  'departure_time',
  'delay_minutes',
  'overdue_hours',
  'downtime_cost',
  'email_delivery',
  'registration_source',
  'work_status',
  'actions',
]

export const COLUMN_LABELS = {
  downtime_classification: 'Класс',
  trip_id: 'Рейс',
  warehouse_name: 'Склад',
  manager_name: 'Менеджер',
  operation_type: 'Операция',
  planned_date: 'План',
  downtime_start: 'Порог',
  arrival_time: 'Прибытие',
  departure_time: 'Убытие',
  delay_minutes: 'Задержка',
  overdue_hours: 'Простой',
  downtime_cost: 'Стоимость',
  email_delivery: 'Email',
  registration_source: 'Регистрация',
  work_status: 'Статус рейса',
  actions: 'Действия',
}

export function defaultFilters() {
  return {
    tripIdPrefix: '',
    workStatusIds: [],
    status: 'all',
    classifications: [],
    operationType: '',
    responsibleIds: [],
    plannedDateFrom: '',
    plannedDateTo: '',
    datePeriod: '',
    includeStale: false,
    page: 1,
    sorting: [{ id: 'planned_date', desc: true }],
  }
}

export function normalizeFilters(value) {
  const source = value && typeof value === 'object' ? value : {}
  const defaults = defaultFilters()
  const status = ['all', 'unregistered', 'registered'].includes(source.status) ? source.status : defaults.status
  const sorting = Array.isArray(source.sorting) && source.sorting.length && SORTABLE_COLUMN_IDS.includes(source.sorting[0]?.id)
    ? [{ id: source.sorting[0].id, desc: Boolean(source.sorting[0].desc) }]
    : defaults.sorting
  return {
    tripIdPrefix: typeof source.tripIdPrefix === 'string' ? source.tripIdPrefix : '',
    workStatusIds: Array.isArray(source.workStatusIds)
      ? [...new Set(source.workStatusIds
        .map(Number)
        .filter((item) => Number.isInteger(item) && item > 0))]
      : [],
    status,
    classifications: Array.isArray(source.classifications)
      ? [...new Set(source.classifications.filter((item) => VALID_CLASSIFICATIONS.includes(item)))]
      : [],
    operationType: ['loading', 'unloading'].includes(source.operationType) ? source.operationType : '',
    responsibleIds: Array.isArray(source.responsibleIds)
      ? [...new Set(source.responsibleIds.filter((item) => typeof item === 'string' && item.trim()))]
      : [],
    plannedDateFrom: normalizeDateFilter(source.plannedDateFrom),
    plannedDateTo: normalizeDateFilter(source.plannedDateTo),
    datePeriod: isKnownPeriod(source.datePeriod) ? source.datePeriod : '',
    includeStale: source.includeStale === true,
    page: Number.isInteger(source.page) && source.page > 0 ? source.page : 1,
    sorting,
  }
}

function normalizeDateFilter(value) {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return ''
  const [year, month, day] = value.split('-').map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day
    ? value
    : ''
}

export function defaultTableState() {
  return normalizeTableState({})
}

export function normalizeTableState(value) {
  const source = value && typeof value === 'object' ? value : {}
  const known = new Set(COLUMN_IDS)
  const order = Array.isArray(source.columnOrder)
    ? [...new Set(source.columnOrder.filter((id) => known.has(id)))]
    : []
  const visibility = {}
  COLUMN_IDS.forEach((id) => {
    if (typeof source.columnVisibility?.[id] === 'boolean') visibility[id] = source.columnVisibility[id]
  })
  if (COLUMN_IDS.every((id) => visibility[id] === false)) visibility[COLUMN_IDS[0]] = true
  const sizing = {}
  COLUMN_IDS.forEach((id) => {
    const size = Number(source.columnSizing?.[id])
    if (Number.isFinite(size) && size >= 18 && size <= 600) sizing[id] = size
  })
  return {
    columnOrder: [...order, ...COLUMN_IDS.filter((id) => !order.includes(id))],
    columnVisibility: visibility,
    columnSizing: sizing,
    highlightMode: typeof source.highlightMode === 'string' ? source.highlightMode : 'none',
    highlightPalette: ['colorful', 'zebra', 'bee'].includes(source.highlightPalette) ? source.highlightPalette : 'colorful',
  }
}

function loadVersionedState(storage, key, fallback, normalize) {
  try {
    const parsed = JSON.parse(storage?.getItem(key) || 'null')
    return parsed?.version === STATE_VERSION ? normalize(parsed.value) : fallback()
  } catch {
    return fallback()
  }
}

export function loadFilters(storage) {
  return materializeDatePeriod(loadVersionedState(storage, FILTERS_STORAGE_KEY, defaultFilters, normalizeFilters))
}

export function materializeDatePeriod(state) {
  if (!state || !state.datePeriod) return state
  const range = resolvePeriodRange(state.datePeriod)
  if (!range) return state
  return { ...state, plannedDateFrom: range.from, plannedDateTo: range.to }
}

export function loadTableState(storage) {
  return loadVersionedState(storage, TABLE_STORAGE_KEY, defaultTableState, normalizeTableState)
}

export function saveFilters(storage, value) {
  saveVersionedState(storage, FILTERS_STORAGE_KEY, normalizeFilters(value))
}

export function saveTableState(storage, value) {
  saveVersionedState(storage, TABLE_STORAGE_KEY, normalizeTableState(value))
}

function saveVersionedState(storage, key, value) {
  try {
    storage?.setItem(key, JSON.stringify({ version: STATE_VERSION, value }))
  } catch {
    return undefined
  }
  return value
}

export function buildDowntimeParams(filters, { includePaging = true } = {}) {
  const state = normalizeFilters(filters)
  const sort = state.sorting[0]
  const params = {
    status: state.status,
    classifications: state.classifications.length ? state.classifications : undefined,
    work_status_ids: state.workStatusIds.length ? state.workStatusIds : undefined,
    trip_id_prefix: state.tripIdPrefix.trim() || undefined,
    operation_type: state.operationType || undefined,
    responsible_ids: state.responsibleIds.length ? state.responsibleIds : undefined,
    planned_date_from: state.plannedDateFrom || undefined,
    planned_date_to: state.plannedDateTo || undefined,
    include_stale: state.includeStale || undefined,
    sort_by: sort.id,
    sort_dir: sort.desc ? 'desc' : 'asc',
  }
  if (includePaging) {
    params.page = state.page
    params.per_page = PAGE_SIZE
  }
  return params
}

export function canRegisterDowntime(item) {
  return Boolean(item?.route_point_id)
    && !item.downtime_registered
    && ['confirmed', 'potential'].includes(item?.downtime_classification)
}

export function canResolveDowntime(item) {
  return Boolean(item?.route_point_id) && item?.downtime_registered === true
}

export function canDownloadDowntimeAct(item) {
  return canResolveDowntime(item)
}

export function buildDowntimeReasonRequest(reason) {
  const value = typeof reason === 'string' ? reason.trim() : ''
  return { reason: value || null }
}
